import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Gift, Plus, Loader2, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { listBonusProjects, createBonusProject, deleteBonusProject, getBonusBuilderQuota } from "@/lib/bonus-builder.functions";

export const Route = createFileRoute("/_authenticated/bonus-builder/")({
  component: BonusBuilderListPage,
});

const STAGE_LABEL: Record<string, string> = {
  setup: "Setup",
  ideas: "Ideas",
  content: "Content",
  delivery: "Sales Copy",
  complete: "Complete",
};

function BonusBuilderListPage() {
  const router = useRouter();
  const list = useServerFn(listBonusProjects);
  const createFn = useServerFn(createBonusProject);
  const delFn = useServerFn(deleteBonusProject);
  const quotaFn = useServerFn(getBonusBuilderQuota);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["bonus-projects"],
    queryFn: () => list(),
  });
  const { data: quota, refetch: refetchQuota } = useQuery({
    queryKey: ["bonus-quota"],
    queryFn: () => quotaFn(),
  });
  const quotaReached = quota && quota.limit != null && quota.used >= quota.limit;

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    target_program_name: "",
    audience_description: "",
    user_expertise: "",
    offer_price_point: "",
    bonus_count: 5,
    target_perceived_value: 2000,
    user_is_recruiting_affiliates: false,
    user_allows_white_label: false,
  });

  const create = useMutation({
    mutationFn: async () => createFn({ data: form }),
    onSuccess: ({ id }) => {
      toast.success("Project created");
      setOpen(false);
      refetchQuota();
      router.navigate({ to: "/bonus-builder/$projectId", params: { projectId: id } });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const del = useMutation({
    mutationFn: async (id: string) => delFn({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); refetch(); refetchQuota(); },
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Gift className="h-6 w-6" /> Bonus Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">AI-powered bonus stacks for the programs you promote.</p>
          {quota && quota.limit != null && (
            <p className="text-xs text-muted-foreground mt-1">
              {quota.used} / {quota.limit} projects used {quota.period === "month" ? "this month" : "during your trial"}
            </p>
          )}
        </div>
        <Button onClick={() => setOpen(true)} disabled={!!quotaReached}><Plus className="h-4 w-4 mr-2" /> New Project</Button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : !data?.projects?.length ? (
        <EmptyState
          icon={Gift}
          title="Build your first bonus stack"
          description="Bonus projects let you design, price, and pitch stacks tied to a target program."
          action={<Button onClick={() => setOpen(true)} disabled={!!quotaReached}><Plus className="h-4 w-4 mr-2" /> New project</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.projects.map((p: any) => (
            <Card key={p.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">
                    <Link to="/bonus-builder/$projectId" params={{ projectId: p.id }} className="hover:underline">
                      {p.target_program_name || "Untitled project"}
                    </Link>
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => { if (confirm("Delete project?")) del.mutate(p.id); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="secondary">{STAGE_LABEL[p.stage] ?? p.stage}</Badge>
                  <Badge variant="outline">{p.bonus_count ?? "?"} bonuses</Badge>
                </div>
                {p.stack_perceived_value ? (
                  <p className="text-sm text-muted-foreground">Stack value: ${Number(p.stack_perceived_value).toLocaleString()}</p>
                ) : p.target_perceived_value ? (
                  <p className="text-sm text-muted-foreground">Target: ${Number(p.target_perceived_value).toLocaleString()}</p>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New bonus project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Program you're promoting</Label>
              <Input value={form.target_program_name} onChange={(e) => setForm({ ...form, target_program_name: e.target.value })} placeholder="e.g. ClickFunnels 2.0" />
            </div>
            <div>
              <Label>Audience description</Label>
              <Textarea value={form.audience_description} onChange={(e) => setForm({ ...form, audience_description: e.target.value })} placeholder="Who are you selling to?" rows={2} />
            </div>
            <div>
              <Label>Your expertise / angle</Label>
              <Textarea value={form.user_expertise} onChange={(e) => setForm({ ...form, user_expertise: e.target.value })} placeholder="What can you uniquely offer?" rows={2} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Offer price</Label>
                <Input value={form.offer_price_point} onChange={(e) => setForm({ ...form, offer_price_point: e.target.value })} placeholder="$297/mo" />
              </div>
              <div>
                <Label># Bonuses (max 5)</Label>
                <Input type="number" min={1} max={5} value={form.bonus_count} onChange={(e) => setForm({ ...form, bonus_count: Math.min(5, Math.max(1, Number(e.target.value) || 5)) })} />
              </div>
              <div>
                <Label>Target value $</Label>
                <Input type="number" value={form.target_perceived_value} onChange={(e) => setForm({ ...form, target_perceived_value: Number(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Recruiting second-tier affiliates?</Label>
                  <p className="text-xs text-muted-foreground">Are you recruiting other affiliates to promote this through you?</p>
                </div>
                <Switch
                  checked={form.user_is_recruiting_affiliates}
                  onCheckedChange={(v) => setForm({ ...form, user_is_recruiting_affiliates: v, user_allows_white_label: v ? form.user_allows_white_label : false })}
                />
              </div>
              {form.user_is_recruiting_affiliates && (
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label>Allow white-label rights?</Label>
                    <p className="text-xs text-muted-foreground">Comfortable letting recruited affiliates use your bonuses as their own?</p>
                  </div>
                  <Switch
                    checked={form.user_allows_white_label}
                    onCheckedChange={(v) => setForm({ ...form, user_allows_white_label: v })}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!form.target_program_name.trim() || create.isPending}>
              {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
